/**
 * The pricing-test-v1 locked arm contract — the one source of truth for the
 * six-leg pricing × free-trial experiment.
 *
 * These keys are durable data, not display labels: they are written to
 * PostHog, Stripe subscription metadata and analytics events, so they must
 * never be renamed or reused after any real assignment. The UI resolver, the
 * server-side quote validator, the plan-transition validator, the lifecycle
 * analytics and the PostHog IaC all consume this module; a change to the arm
 * KEYS or PRICES here is a change to the experiment's arms, which is
 * forbidden mid-flight. The SPLIT may move — down to zero, which retires an
 * arm (see `PRICING_TEST_V1_SPLIT`).
 *
 * Deliberately a zero-import leaf module (like `free-plan-countries`) so the
 * IaC scripts, the API and the web client can all deep-import it.
 *
 * Full contract: docs/projects/active/pricing-experiment-ab-test-6-leg.md
 * ("Locked arm contract").
 */

/**
 * The experiment's key, in all three places it names the same thing: the
 * `EdgeBucketing.pricingTestV1` definition the edge/lambda/API hash against,
 * the `experiment_assignment.experiment_key` column the account pin is stored
 * under, and the PostHog flag the analysis reads.
 *
 * **Assignment is ours, not PostHog's.** The arm is computed from the
 * `lis_bucket` cookie at the CloudFront viewer-request function and frozen onto
 * the account at signup; the API re-resolves it per request through the same
 * ladder (pin → bucket → `fallbackVariant`). PostHog never answers "what
 * variant" for this experiment — it cannot, since the top rung is a per-account
 * database row and CloudFront Functions cannot do I/O. Its flag definition
 * exists so the experiment view has a vocabulary to analyze the
 * `$feature/pricing-test-v1` super-property against, and an outage in it
 * degrades analytics only, never a charged price.
 */
export const PRICING_TEST_V1_FLAG_KEY = 'pricing-test-v1';

/** Stripe subscription-metadata key carrying the assigned variant. The only
 * thing that separates leg 1 from leg 4 in Stripe (both $39). */
export const PRICING_VARIANT_METADATA_KEY = 'pricing_variant';

/** Stripe subscription-metadata key binding a checkout attempt to the quote
 * it was created for (idempotency-key derivation and retry convergence). */
export const CHECKOUT_ATTEMPT_METADATA_KEY = 'checkout_attempt_id';

export const PRICING_TEST_V1_VARIANTS = [
  'control',
  'price-59-no-trial',
  'price-79-no-trial',
  'price-39-trial',
  'price-59-trial',
  'price-79-trial',
] as const;

export type PricingTestV1Variant = (typeof PRICING_TEST_V1_VARIANTS)[number];

/** Annual price tier in whole USD. Monthly is $12.99 in every arm. */
export type PricingTestAnnualTierUsd = 39 | 59 | 79;

export interface PricingTestArm {
  annualTierUsd: PricingTestAnnualTierUsd;
  /**
   * Nominal trial days for the arm. Server-side eligibility derives the
   * effective value (an account with any retrievable prior Stripe
   * subscription gets 0), but the assigned variant — including its
   * trial dimension — is retained for intention-to-treat analysis.
   */
  trialDays: 0 | 3;
}

export const PRICING_TEST_V1_ARMS: Record<
  PricingTestV1Variant,
  PricingTestArm
> = {
  control: { annualTierUsd: 39, trialDays: 0 },
  'price-59-no-trial': { annualTierUsd: 59, trialDays: 0 },
  'price-79-no-trial': { annualTierUsd: 79, trialDays: 0 },
  'price-39-trial': { annualTierUsd: 39, trialDays: 3 },
  'price-59-trial': { annualTierUsd: 59, trialDays: 3 },
  'price-79-trial': { annualTierUsd: 79, trialDays: 3 },
};

/**
 * The share of enrolled traffic each arm receives, summing to exactly 1.
 *
 * The two $79 arms are RETIRED at weight 0: they stay in the contract because
 * their keys are on live Stripe subscriptions, pinned accounts and PostHog
 * events, but no new visitor is assigned to them, and an account pinned to one
 * is quoted the arm that absorbed its hash range — the arm before it below —
 * without being a subject (`ExperimentAssignments.resolveArm`, via
 * `absorbingVariant`). Launched 2026-08-25 as six arms at
 * `17/17/17/17/16/16`; the $79 tier was withdrawn from both trial legs before
 * the read-out, so every $79 comparison must be windowed to the retirement
 * (`scripts/reports/pricing-experiment.ts --until`).
 *
 * The weights are NOT rebalanced to equal quarters, on purpose. `pickVariant`
 * walks the split in order and assigns by cumulative range, so a visitor's
 * arm is where their hash falls between the running totals. The launch
 * boundaries were 0.17 / 0.34 / 0.51 / 0.68 / 0.84 / 1; folding each retired
 * arm's share into the arm BEFORE it (`price-59-no-trial` absorbs the
 * `price-79-no-trial` range, `price-59-trial` absorbs `price-79-trial`'s)
 * keeps every surviving boundary bit-identical, so the only cookies that
 * re-roll are the ones that were in a $79 range — and they land on the $59 arm
 * that a pin on the arm they left is now quoted, trial dimension included.
 * Equal quarters would
 * have moved ~47% of `price-59-no-trial` cookies to control and ~43% of
 * `price-59-trial` cookies to `price-39-trial`: a pinned-but-unpaid account
 * returning on its signup cookie would then read $39 on `/pricing` (an
 * anonymous, cookie-rendered surface) and be quoted $59 at the paywall — the
 * advertised-below-charged inversion C4 forbids.
 *
 * The sum is exactly 1 in floating point (asserted), which matters because
 * `pickVariant` hands anything past the running total to the LAST entry, and
 * that entry is now a retired arm.
 */
export const PRICING_TEST_V1_SPLIT: Record<PricingTestV1Variant, number> = {
  control: 0.17,
  'price-59-no-trial': 0.34,
  'price-79-no-trial': 0,
  'price-39-trial': 0.17,
  'price-59-trial': 0.32,
  'price-79-trial': 0,
};

/**
 * The single-character code each arm is known by AT THE EDGE, and nowhere else.
 *
 * The CloudFront viewer-request function hashes and stamps these codes rather
 * than the arm keys above, because the arm keys do not fit: measured, a six-arm
 * definition carrying them puts the viewer-request bundle 350 bytes over the
 * 3,674-byte budget asserted in `packages/infra/src/web/edge.ts`, and no combination of
 * available savings closes that. The codes cost 185 bytes less and do fit. See
 * docs/projects/active/edge-experiments-account-pinned-bucketing.md §7.
 *
 * **The code is a transport encoding, not an identity.** It appears in exactly
 * three places — the `split` keys the edge/lambda/API hash against, the
 * `x-variants` response header, and the `experiment_assignment.variant` rows
 * pinned from them. Everything durable and human-read carries the ARM KEY:
 * Stripe `pricing_variant` metadata, the PostHog flag variants and
 * `$feature/pricing-test-v1` property, analytics events, and this module's
 * price map. Cross the boundary with {@link decodePricingEdgeCode} /
 * {@link edgeCodeForPricingArm} — never by hand.
 *
 * Codes are assigned in `PRICING_TEST_V1_VARIANTS` order and are as frozen as
 * the arm keys: they are hash inputs and pinned-row values, so re-lettering
 * re-rolls every anonymous visitor and strands every pinned account. The
 * `satisfies` makes an unmapped arm a compile error.
 */
export const PRICING_TEST_V1_EDGE_CODES = {
  control: 'a',
  'price-59-no-trial': 'b',
  'price-79-no-trial': 'c',
  'price-39-trial': 'd',
  'price-59-trial': 'e',
  'price-79-trial': 'f',
} as const satisfies Record<PricingTestV1Variant, string>;

export type PricingTestV1EdgeCode =
  (typeof PRICING_TEST_V1_EDGE_CODES)[PricingTestV1Variant];

const PRICING_ARM_BY_EDGE_CODE: Record<
  string,
  PricingTestV1Variant | undefined
> = Object.fromEntries(
  PRICING_TEST_V1_VARIANTS.map(
    (variant) => [PRICING_TEST_V1_EDGE_CODES[variant], variant] as const,
  ),
);

/** The edge code for an arm. Total — every arm has one by construction. */
export const edgeCodeForPricingArm = (
  variant: PricingTestV1Variant,
): PricingTestV1EdgeCode => PRICING_TEST_V1_EDGE_CODES[variant];

/**
 * Decode an edge code read back from `x-variants`, a pinned row, or an API
 * response. Returns null for anything that is not a live code — a code from a
 * retired arm, a full arm key sent where a code belongs, `false` from a flag
 * outage. Callers decide the policy, exactly as {@link decodePricingVariant}
 * does for arm keys.
 */
export const decodePricingEdgeCode = (
  value: unknown,
): PricingTestV1Variant | null =>
  typeof value === 'string' ? (PRICING_ARM_BY_EDGE_CODE[value] ?? null) : null;

/**
 * Decode a `pricing_variant` read back from Stripe metadata, a PostHog flag
 * value, or a client request. Returns null for anything that is not a
 * recognized arm — including `false`, `'control-ish'` typos and empty
 * strings. Callers decide the policy: a null from a flag read means
 * "unenrolled, quote control, stamp nothing"; an unknown non-null value on a
 * trialing subscription is an invariant violation and fails closed.
 */
export const decodePricingVariant = (
  value: unknown,
): PricingTestV1Variant | null =>
  typeof value === 'string' &&
  (PRICING_TEST_V1_VARIANTS as readonly string[]).includes(value)
    ? (value as PricingTestV1Variant)
    : null;

/** True when the arm offers a trial (nominal — before eligibility). */
export const pricingVariantHasTrial = (
  variant: PricingTestV1Variant,
): boolean => PRICING_TEST_V1_ARMS[variant].trialDays > 0;

/**
 * C4 as an enforceable rule: every viewer the edge cannot bucket — crawlers,
 * cookie-less first requests — is served `fallbackVariant` with no exposure
 * event, so if that arm were cheaper than an arm checkout can charge, a
 * search engine could index a price the checkout will not honour. Bot
 * detection missing an unknown crawler stays survivable only because of this
 * rule (Mode C design §3.5, C4).
 *
 * Only arms the split still assigns count as "an arm checkout can charge": a
 * retired arm (weight 0) admits no new visitor, and an account already pinned
 * to one is quoted the fallback rather than its pinned price. The fallback
 * itself must be a live arm — a retired fallback would advertise a price no
 * visitor can be assigned.
 *
 * Accepts both vocabularies — edge codes (`a`–`f`) and full arm keys — so it
 * can judge the edge definition and test fixtures alike. Returns null when
 * the rule holds, or the sentence to fail with. The pricing edge definition
 * asserts this at module load (`edge-client-flags.ts`), so a bad fallback is
 * a refused definition, not a test failure someone has to run.
 */
export const pricingFallbackViolation = (def: {
  readonly key: string;
  readonly variants: readonly string[];
  readonly split: Readonly<Record<string, number>>;
  readonly fallbackVariant: string;
}): string | null => {
  const decode = (value: string): PricingTestV1Variant | null =>
    decodePricingVariant(value) ?? decodePricingEdgeCode(value);
  const annualUsd = (variant: PricingTestV1Variant): number =>
    PRICING_TEST_V1_ARMS[variant].annualTierUsd;
  const isLive = (name: string): boolean => (def.split[name] ?? 0) > 0;

  const fallback = decode(def.fallbackVariant);
  if (fallback === null) {
    return `${def.key} declares fallbackVariant "${def.fallbackVariant}", which is not a pricing-test-v1 arm, so no price can be implied for the viewers who receive it.`;
  }
  if (!isLive(def.fallbackVariant)) {
    return `${def.key} declares fallbackVariant "${def.fallbackVariant}", which the split no longer assigns; crawlers and cookie-less viewers would be advertised a price no visitor can be quoted.`;
  }
  let topArm = fallback;
  for (const name of def.variants) {
    const arm = decode(name);
    if (arm === null) {
      return `${def.key} lists variant "${name}", which is not a pricing-test-v1 arm; a price experiment's arms must all be priceable.`;
    }
    if (isLive(name) && annualUsd(arm) > annualUsd(topArm)) topArm = arm;
  }
  if (annualUsd(fallback) >= annualUsd(topArm)) return null;
  return `${def.key} declares fallbackVariant "${fallback}" ($${annualUsd(fallback)}/yr) while arm "${topArm}" charges $${annualUsd(topArm)}/yr. Crawlers and cookie-less viewers are served the fallback, so this advertises $${annualUsd(fallback)} for a page whose checkout can charge $${annualUsd(topArm)} — the advertised-below-charged direction design C4 exists to make impossible.`;
};
